import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { GridColumnMenu } from "@mui/x-data-grid";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CommentIcon from "@mui/icons-material/Chat";
import PropTypes from "prop-types";

import Modal from "Components/Common/Modal";
import CommentInputForReason from "Components/Common/CommentInputForReason";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import CommentBox from "Components/Common/CommentBox";
import { powerAdmin4Roles } from "common/constant";
import { getOptionValue } from "common/enums";

import { isAuthorizedUser } from "utils";
import { getBillingApprovalStatusForOtherUtilities } from "utils/getApprovalStatusFOrOtherUtilities";
import { getBillingApprovalStatus } from "utils/getBillingApprovalStatus";

import {
  useGetBillingCalculationQuery,
  usePutBillingCalculation,
} from "apis/Billing";
import { useLazyAdminCommentQuery } from "apis/ApplicationRequestComment";

import { StyledGrid } from "./Style";

const subscriptionWiseUtility = {
  [getOptionValue("subscriptionTypes", "DCS")]: [2],
  [getOptionValue("subscriptionTypes", "Water & Sewage")]: [3],
  [getOptionValue("subscriptionTypes", "Solid Waste")]: [5],
};

const CustomColumnMenu = (props) => (
  <GridColumnMenu
    {...props}
    slots={{
      columnMenuColumnsItem: null,
      columnMenuSortItem: null,
    }}
  />
);

const BillReviewModel = ({ handleClose, t, subscriptionType }) => {
  const { roles } = useSelector((state) => state.Login);

  const [searchParams] = useSearchParams();
  const billingCycleId = searchParams.get("billingCycleId");

  const {
    data: { data: billCalculationsData } = {},
    isLoading: isBillingCalculationLoading,
  } = useGetBillingCalculationQuery(billingCycleId);

  const [getAdminComments, { data: { data: commentsData = [] } = {} }] =
    useLazyAdminCommentQuery();

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [isApproveBtnClicked, setIsApproveBtnClicked] = useState(false);
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [consumerBillId, setConsumerBillId] = useState(false);

  const [isRevokedCommentModalVisible, setIsRevokedCommentModalVisible] =
    useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [updateBillCalculationsStatus, { isLoading }] =
    usePutBillingCalculation();

  const isOtherUtility = useMemo(
    () =>
      subscriptionType !== getOptionValue("subscriptionTypes", "Electricity"),
    [subscriptionType]
  );

  const billCalculations = useMemo(
    () =>
      billCalculationsData?.filter(({ approvedBy, revokedBy, status }) =>
        isOtherUtility
          ? getBillingApprovalStatusForOtherUtilities(
              { approvedBy, revokedBy, status },
              roles,
              subscriptionWiseUtility[subscriptionType]
            ).canApproved
          : getBillingApprovalStatus({ approvedBy, revokedBy, status }, roles)
              .canApprove
      ) ?? [],
    [billCalculationsData]
  );

  const isAllBillCalculationApproved = useMemo(
    () =>
      !billCalculations?.some(({ approvedBy, revokedBy }) =>
        isOtherUtility
          ? getBillingApprovalStatusForOtherUtilities(
              { approvedBy, revokedBy },
              roles,
              subscriptionWiseUtility[subscriptionType]
            ).canApproved
          : getBillingApprovalStatus({ approvedBy, revokedBy }, roles)
              .canApprove
      ),
    [billCalculations]
  );

  const canBillCalculationRevoked = useMemo(
    () =>
      !billCalculations?.some(({ approvedBy, revokedBy }) =>
        isOtherUtility
          ? getBillingApprovalStatusForOtherUtilities(
              { approvedBy, revokedBy },
              roles,
              subscriptionWiseUtility[subscriptionType]
            ).canRevoked
          : getBillingApprovalStatus({ approvedBy, revokedBy }, roles).canRevoke
      ),
    [billCalculations]
  );

  const columns = useMemo(
    () => [
      {
        field: "consumerNumber",
        headerName: "Consumer Number",
        width: 150,
      },
      { field: "consumerName", headerName: "Consumer Name", width: 350 },
      {
        field: "billCycle",
        headerName: "Bill Cycle",
        width: 150,
      },
      { field: "meterNumber", headerName: "Meter Number", width: 150 },
      { field: "netPayable", headerName: "Net Payable" },
      {
        field: "comments",
        headerName: "Comment",
        renderCell: ({ row: { id } }) => (
          <IconButton onClick={() => setConsumerBillId(id)}>
            <CommentIcon color="primary" />
          </IconButton>
        ),
        width: 190,
      },
    ],
    [billCalculations]
  );

  const approveBtnHandler = async () => {
    try {
      await updateBillCalculationsStatus(
        {
          billCalculationIds: selectedRowIds,
          isApproved: true,
        },
        "Bill calculations approved successfully"
      );
    } catch (error) {}
  };

  const revokeBtnHandler = async (comment) => {
    try {
      await updateBillCalculationsStatus(
        {
          billCalculationIds: selectedRowIds,
          isApproved: false,
          comment,
        },
        "Bill calculations revoked successfully"
      );
    } catch (error) {}
  };

  useEffect(() => {
    try {
      if (consumerBillId) {
        (async () =>
          await getAdminComments({
            commentType: getOptionValue("adminComments", "BillingCalculation"),
            commentTypeId: consumerBillId,
          }))();
      }
    } catch (error) {
      console.error(error);
    }
  }, [consumerBillId]);

  return (
    <>
      {!!consumerBillId && (
        <Modal
          isOpen
          title="Comments"
          maxWidth="md"
          handleClose={() => setConsumerBillId("")}
        >
          <div className="mt-1">
            {commentsData?.length ? (
              commentsData.map(({ comment, createdAt, name }, index) => (
                <CommentBox
                  comment={comment}
                  name={name}
                  createdAt={createdAt}
                  key={index}
                />
              ))
            ) : (
              <Card variant="outlined" className="mt-2 p-0">
                <CardContent className="pt-1">
                  <Typography color="GrayText" gutterBottom>
                    {t("No comment")}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </div>
        </Modal>
      )}
      <CommentInputForReason
        handleClose={() => {
          setIsRevokedCommentModalVisible(false);
        }}
        handleSubmit={(comment) => {
          revokeBtnHandler(comment);
          setIsRevokedCommentModalVisible(false);
        }}
        isOpen={isRevokedCommentModalVisible}
        isLoading={isLoading}
        title="Add Reason for Revoked"
        errorMessage="Please enter reason for revoked"
      />
      <ConfirmationDialog
        message="Are you sure you want to Approve selected bill calculations?"
        onCancel={() => setIsApproveBtnClicked(false)}
        onConfirm={() => {
          approveBtnHandler();
          setIsApproveBtnClicked(false);
        }}
        isOpen={isApproveBtnClicked}
        isLoading={isLoading}
        title="Warning"
        variant="warning"
      />

      <Modal
        isOpen
        handleClose={handleClose}
        title="Review Bills"
        maxWidth="lg"
      >
        {isBillingCalculationLoading ? (
          <div className="d-flex justify-content-center p-4">
            <CircularProgress size={30} />
          </div>
        ) : (
          <>
            <StyledGrid
              checkboxSelection
              autoHeight
              disableRowSelectionOnClick
              rows={billCalculations}
              columns={columns}
              pageSizeOptions={[5, 10]}
              rowCount={billCalculations?.length}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              onRowSelectionModelChange={(ids) => {
                const selectedIDs = Array.from(
                  // eslint-disable-next-line no-undef
                  new Set([...ids.flatMap((id) => id)])
                );
                const billCalculationsIds = billCalculations.flatMap(
                  ({ id }) => id
                );
                const selectedBillIds = selectedIDs.filter((id) =>
                  billCalculationsIds.includes(id)
                );
                setIsRowSelected(!!selectedBillIds.length);
                setSelectedRowIds(selectedBillIds);
              }}
              slots={{
                columnMenu: CustomColumnMenu,
                noRowsOverlay: () => (
                  <Stack
                    height="100%"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {t("No records found")}.
                  </Stack>
                ),
              }}
              rowSelection
              keepNonExistentRowsSelected
            />
            <div className="text-end">
              {!isAuthorizedUser(roles, powerAdmin4Roles) && (
                <Button
                  variant="contained"
                  className="mt-2 me-2"
                  color="primary"
                  disabled={canBillCalculationRevoked || !isRowSelected}
                  onClick={() => setIsRevokedCommentModalVisible(true)}
                >
                  {t("Revoke")}
                </Button>
              )}
              <Button
                variant="contained"
                className="mt-2 "
                color="success"
                disabled={isAllBillCalculationApproved || !isRowSelected}
                onClick={() => {
                  setIsApproveBtnClicked(true);
                }}
              >
                {t("Approve")}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

BillReviewModel.propTypes = {
  handleClose: PropTypes.func,
  subscriptionType: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(BillReviewModel);
